import { writer } from "@/backend/write/BackendWriter";
import type { Id } from "@/model/baseTypes";
import type { Card } from "@/model/card";
import type { CardLink, Link, StickyLink } from "@/model/link";
import { useBoardStore } from "@/store/board";
import { useBoardsStore } from "@/store/boards";
import { useCardStore } from "@/store/card";
import { useDraggingStore } from "@/store/dragging";
import { animateLinkMove } from "@/store/helpers/animator";
import { resetCardMark } from "@/store/helpers/mark";
import { useLinkStore } from "@/store/link";
import { useToastStore } from "@/store/toast";

import { ctxAction, defineActions, untrackedAction } from "./actions";
import { internalActions } from "./internalActions";
import type { ActionContext } from "./types";

export const linkActions = defineActions("link", {
  add: ctxAction(async (ctx, cardLink: { fromId: Link["from"]; toId: Link["to"] }) => {
    ctx.track({
      fromId: cardLink.fromId,
      fromFunctionality: useCardStore().cards[cardLink.fromId]?.type.functionality,
      fromBoard: useBoardStore().currentBoard().type,
      toId: cardLink.toId,
      toFunctionality: useCardStore().cards[cardLink.toId]?.type.functionality,
    });
    const link = await writer.addLink(cardLink.fromId, cardLink.toId);
    // TODO this is not strictly needed as we get also a subscription when a link is created
    // but is this garanteed? how long can it take to get it?
    // part of the general question "how to handle creating a new persisted object"
    useLinkStore().add({
      id: link.id,
      from: link.from_sticky_id,
      to: link.to_sticky_id,
      type: link.link_type_id,
      state: "default",
    });
  }),
  remove: ctxAction(async (ctx, linkId: Link["id"], info?: { from: string; to: string }) => {
    ctx.track({
      fromId: info?.from,
      fromFunctionality: useCardStore().cards[info?.from || ""]?.type.functionality,
      fromBoard: useBoardStore().currentBoard().type,
      toId: info?.to,
      toFunctionality: useCardStore().cards[info?.to || ""]?.type.functionality,
    });
    await writer.deleteLink(linkId);
    // this is used for performance reasons, there is a second call in subscriptionHandlers
    useLinkStore().remove(linkId);
    internalActions.updateCardLinkedMarks();
  }),
  addObjective: ctxAction(addObjective),
  removeObjective: ctxAction(removeObjective),
  setCardLinkLabel: untrackedAction(async (_ctx, card: Card, cardLink: CardLink) => {
    if (cardLink.kind === "sticky") {
      await recreateStickyLink(cardLink);
    } else {
      await recreateObjectiveLink(card, cardLink.boardId, cardLink.objectiveId);
    }

    async function recreateStickyLink(stickyLink: StickyLink) {
      const link = useLinkStore().linksById[stickyLink.id];
      await linkActions.remove("internal", stickyLink.id);
      if (!link) return;

      await linkActions.add("internal", { fromId: link.from, toId: link.to });
    }

    async function recreateObjectiveLink(card: Card, boardId: string, objectiveId: string) {
      await linkActions.removeObjective("internal", card.id, boardId, objectiveId);
      await linkActions.addObjective("internal", card.id, boardId, objectiveId);
    }
  }),

  addByDrag: untrackedAction(async (ctx, dragInfo: Id & { el: HTMLElement; dragId: number }) => {
    const currentBoard = useBoardStore().currentBoard();
    const boardCard = currentBoard.cards[dragInfo.id];
    const { from: linkFrom, to: linkTo } = useLinkStore().linking;

    useLinkStore().linking.from = null;
    useLinkStore().linking.to = null;
    ctx.track(null);
    if (linkFrom && linkTo) {
      if (linkTo.type === "sticky") {
        await linkActions.add("drag", { fromId: linkFrom.id, toId: linkTo.id });
        resetCardMark(useBoardStore().currentBoard().cards[linkTo.id]);
        internalActions.updateCardLinkedMarks();
      } else {
        await linkActions.addObjective("drag", linkFrom.id, currentBoard.id, linkTo.id);
      }
      useDraggingStore().endLinkDragging(dragInfo.id, dragInfo.dragId, boardCard, dragInfo.el);
    } else {
      animateLinkMove(
        boardCard,
        useDraggingStore().dragging[dragInfo.dragId].pos,
        (coord) => useDraggingStore().dragLink(dragInfo.dragId, coord),
        () =>
          useDraggingStore().endLinkDragging(dragInfo.id, dragInfo.dragId, boardCard, dragInfo.el),
      );
    }
  }),
});

async function addObjective(
  ctx: ActionContext,
  cardId: string,
  boardId: string,
  objectiveId: string,
) {
  ctx.track(getLinkObjectiveData(cardId, boardId, objectiveId));
  await writer.addObjectiveLink(cardId, boardId, objectiveId);
  useLinkStore().addObjectiveLink(cardId, { boardId, objectiveId });
}

async function removeObjective(
  ctx: ActionContext,
  cardId: string,
  boardId: string,
  objectiveId: string,
) {
  ctx.track(getLinkObjectiveData(cardId, boardId, objectiveId));
  await writer.deleteObjectiveLink(cardId, boardId, objectiveId);
  useLinkStore().removeObjectiveLink(cardId, { boardId, objectiveId });

  // Notify screen reader users that link was removed
  useToastStore().show(/*$t*/ "linkableCardRow.linkRemoved", { invisible: true });
}

function getLinkObjectiveData(cardId: string, boardId: string, objectiveId: string) {
  const fromCard = useCardStore().cards[cardId];
  return {
    fromId: cardId,
    // link could be made to a card on a different team (the card could be not loaded yet)
    // fromFunctionality is only needed for tracking, so it is okay to for it to be undefined
    fromFunctionality: fromCard?.type.functionality,
    fromBoard: useBoardStore().currentBoard().type,
    toId: objectiveId,
    toBoard: useBoardsStore().boardById(boardId).type,
  };
}
